import { FormEvent, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '../../components/auth/AuthLayout';
import { FormError, PasswordField, SubmitButton, scorePassword } from '../../components/auth/FormFields';
import { authApi, readApiError } from '../../lib/auth-api';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (password !== confirm) {
      setErr(t('auth.errorPasswordMismatch'));
      return;
    }
    if (scorePassword(password) < 2) {
      setErr(t('auth.errorPasswordWeak'));
      return;
    }
    setBusy(true);
    try {
      await authApi.resetPassword(token, password);
      navigate('/login?reset=ok');
    } catch (e) {
      setErr(readApiError(e, t('auth.errorTokenInvalid')));
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout
        eyebrow={t('auth.resetEyebrow')}
        title={t('auth.errorTokenInvalid')}
        subtitle={t('auth.resetMissingTokenBody')}
        altCta={{ to: '/forgot-password', helper: t('auth.requestNewLink'), label: t('auth.forgotTitle') }}
      >
        <div className="text-sm text-muted-foreground">{t('auth.resetMissingTokenBody')}</div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      eyebrow={t('auth.resetEyebrow')}
      title={t('auth.resetTitle')}
      subtitle={t('auth.resetSubtitle')}
      altCta={{ to: '/login', helper: t('auth.backTo'), label: t('auth.signInLink') }}
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <PasswordField
          label={t('auth.newPassword')}
          autoComplete="new-password"
          required
          showStrength
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <PasswordField
          label={t('auth.confirmPassword')}
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        {err && <FormError>{err}</FormError>}
        <SubmitButton loading={busy}>{t('auth.resetButton')}</SubmitButton>
      </form>
    </AuthLayout>
  );
}
